import { ethers } from "ethers";
import {
  UXDClient,
  Address,
  calculateMinAmountOut,
} from "@uxdprotocol/uxd-evm-client";
import { MetaMaskInpageProvider } from "@metamask/providers";
import { IArbitrumConfiguration, TokenInfo } from "../config/arbitrum.types";
import logger from "../utils/logger";
import { MaybePromise } from "../types";

export class UXDClientService {
  constructor(
    protected provider:
      | ethers.providers.ExternalProvider
      | MetaMaskInpageProvider
      | null,
    protected rpcProvider: ethers.providers.JsonRpcProvider,
    protected config: IArbitrumConfiguration,
    protected client: UXDClient
  ) {}

  public static initialize = async ({
    config,
    rpcProvider,
    provider,
  }: {
    config: IArbitrumConfiguration;
    rpcProvider: ethers.providers.JsonRpcProvider;
    provider: MaybePromise<
      ethers.providers.ExternalProvider | MetaMaskInpageProvider | null
    >;
  }) => {
    const client = new UXDClient({
      provider: rpcProvider,
      controller: config.contracts.controller.address,
      redeemable: config.tokens.UXD.address,
    });

    const resolvedProvider = provider ? await provider : null;

    return new UXDClientService(resolvedProvider, rpcProvider, config, client);
  };

  public async getTokenBalance(params: {
    token: Address;
    account: Address;
    decimals: number;
  }) {
    return this.client.uxdController.getTokenBalance(params);
  }

  private getSigner() {
    return new ethers.providers.Web3Provider(
      this.provider! as unknown as ethers.providers.ExternalProvider,
      "any"
    ).getSigner();
  }

  public async mint({
    collateralAmount,
    collateralPrice,
    slippage,
    collateral,
    collateralDecimals,
  }: {
    collateralAmount: number;
    collateralPrice: number;
    slippage: number;
    collateral: TokenInfo["address"];
    collateralDecimals: number;
  }): Promise<string> {
    const signer = this.getSigner();

    const { decimals: redeemableDecimals } =
      window.__UXD__.arbitrum.config.tokens.UXD;

    const minAmountOut = calculateMinAmountOut(
      collateralAmount * collateralPrice,
      slippage,
      redeemableDecimals
    );

    logger.log("Arbitrum: Attempting to mint ...", {
      signer,
      collateralAmount,
      minAmountOut,
      collateral,
    });

    await this.client.uxdController.approveERC20TokenTransfer({
      token: collateral,
      spender: this.config.contracts.controller.address,
      amount: collateralAmount,
      signer,
      decimals: collateralDecimals,
    });

    const tx = await this.client.uxdController.mint({
      signer,
      amount: collateralAmount,
      minAmountOut,
      collateral,
      redeemableDecimals,
      collateralDecimals,
    });

    const receipt = await tx.wait();
    return receipt.transactionHash;
  }

  public async redeem({
    redeemableAmount,
    slippage,
    collateral,
    collateralDecimals,
    collateralPrice,
  }: {
    redeemableAmount: number;
    slippage: number;
    collateral: TokenInfo["address"];
    collateralDecimals: number;
    collateralPrice: number;
  }): Promise<string> {
    const signer = this.getSigner();
    const redeemableValue = (1 / collateralPrice) * redeemableAmount;
    const minAmountOut = calculateMinAmountOut(
      redeemableValue,
      slippage,
      collateralDecimals
    );

    logger.log("Arbitrum: Attempting to redeem ...", {
      signer,
      redeemableAmount,
      minAmountOut,
      collateral,
    });

    const { decimals: redeemableDecimals } =
      window.__UXD__.arbitrum.config.tokens.UXD;

    await this.client.uxdController.approveUXDTransfer({
      amount: redeemableAmount,
      signer,
      spender: this.config.contracts.controller.address,
      decimals: redeemableDecimals,
    });

    const tx = await this.client.uxdController.redeem({
      signer,
      amount: redeemableAmount,
      minAmountOut,
      collateralDecimals,
      redeemableDecimals,
      collateral,
    });
    const receipt = await tx.wait();
    return receipt.transactionHash;
  }
}
